import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import CadastroRapidoModal from "../../components/estoque/CadastroRapidoModal";
import ProductAutocomplete from "../../components/ProductAutocomplete";
import {
  entradaManualService,
  produtoService,
} from "../../services/apiService";
import { Button, SectionCard, TextInput } from "../../components/ui";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import { useNotification } from "../../hooks/useNotification";
import EntradaPedidoLote from "./EntradaPedidoLote";

const novoId = () => crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`;

function EntradaManualAvulsa() {
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useNotification();
  const confirmar = useConfirmDialog();
  const [produtoAvulsoId, setProdutoAvulsoId] = useState("");
  const [buscaProduto, setBuscaProduto] = useState("");
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [quantidade, setQuantidade] = useState(1);
  const [observacao, setObservacao] = useState("");
  const [cadastroRapido, setCadastroRapido] = useState(false);

  const produtos = useQuery({
    queryKey: ["produtos-ativos", "busca", buscaProduto],
    queryFn: () =>
      produtoService.listar({
        busca: buscaProduto,
        situacao: "ATIVO",
        tamanho: 501,
      }),
    enabled: buscaProduto.trim().length >= 3,
    retry: 0,
  });
  const saldo = useQuery({
    queryKey: ["saldo-entrada", produtoAvulsoId],
    queryFn: () => entradaManualService.saldo(produtoAvulsoId),
    enabled: Boolean(produtoAvulsoId),
    retry: 0,
  });
  const opcoes = useMemo(
    () =>
      (produtos.data || []).map((item) => ({
        value: item.id,
        label: `${item.descricao}${item.codigo ? ` · ${item.codigo}` : ""}`,
      })),
    [produtos.data],
  );

  const salvar = async (evento) => {
    evento.preventDefault();
    if (!produtoAvulsoId || !saldo.data) return;

    const confirmado = await confirmar({
      title: "Registrar entrada sem pedido",
      description: `Registrar ${quantidade} ${
        saldo.data.unidadeSimbolo || "unidade(s)"
      } no estoque?`,
      variant: "primary",
    });
    if (!confirmado) return;

    try {
      await entradaManualService.registrarAvulsa({
        produtoId: produtoAvulsoId,
        quantidade: Number(quantidade),
        observacao,
        expectedVersion: saldo.data.versaoSaldo,
        requestId: novoId(),
      });
      showSuccess("Entrada sem pedido registrada.");
      setProdutoAvulsoId("");
      setProdutoSelecionado(null);
      setBuscaProduto("");
      setQuantidade(1);
      setObservacao("");
      queryClient.invalidateQueries({ queryKey: ["produtos-ativos"] });
    } catch (erro) {
      showError(erro.message);
    }
  };

  return (
    <>
      <SectionCard>
        <form onSubmit={salvar} className="space-y-5">
          <div>
            <h2 className="font-semibold text-gray-900">Entrada sem pedido</h2>
            <p className="mt-1 text-sm text-gray-500">
              Use quando a entrada não estiver vinculada a uma lista de compras.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-sm font-medium text-gray-700">
              Produto
              <ProductAutocomplete
                value={produtoAvulsoId}
                onChange={(id) => {
                  setProdutoAvulsoId(id);
                  setProdutoSelecionado(
                    opcoes.find((opcao) => opcao.value === id) || null,
                  );
                }}
                placeholder="Digite ao menos 3 letras do produto"
                options={opcoes}
                onSearch={setBuscaProduto}
                searchLoading={produtos.isFetching}
                selectedOption={produtoSelecionado}
              />
              {produtos.isError && (
                <p className="mt-1 text-sm text-red-600">
                  Não foi possível buscar os produtos. Tente novamente.
                </p>
              )}
              <button
                type="button"
                onClick={() => setCadastroRapido(true)}
                className="mt-2 rounded border border-blue-300 px-3 py-2 text-sm text-blue-700 hover:bg-blue-50"
              >
                Novo produto
              </button>
            </label>

            <Campos
              quantidade={quantidade}
              setQuantidade={setQuantidade}
              observacao={observacao}
              setObservacao={setObservacao}
              saldo={saldo.data}
            />
          </div>

          {saldo.isError && (
            <p className="text-sm text-red-600">{saldo.error.message}</p>
          )}

          <div className="flex justify-end">
            <Button
              type="submit"
              variant="success"
              icon={Save}
              disabled={!produtoAvulsoId || saldo.isLoading}
            >
              Registrar entrada sem pedido
            </Button>
          </div>
        </form>
      </SectionCard>

      <CadastroRapidoModal
        open={cadastroRapido}
        tipo="produto"
        onClose={() => setCadastroRapido(false)}
        onCreated={(produto) => {
          setProdutoAvulsoId(produto.id);
          setProdutoSelecionado({
            value: produto.id,
            label: `${produto.descricao}${
              produto.codigo ? ` · ${produto.codigo}` : ""
            }`,
          });
          setCadastroRapido(false);
          queryClient.invalidateQueries({ queryKey: ["produtos-ativos"] });
        }}
      />
    </>
  );
}

function Campos({
  quantidade,
  setQuantidade,
  observacao,
  setObservacao,
  saldo,
}) {
  return (
    <>
      <label className="text-sm font-medium text-gray-700">
        Quantidade
        <TextInput
          required
          type="number"
          min="1"
          value={quantidade}
          onChange={(e) => setQuantidade(e.target.value)}
          className="mt-1 text-right"
        />
      </label>
      <label className="text-sm font-medium text-gray-700">
        Observação
        <TextInput
          maxLength={1000}
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="Opcional"
          className="mt-1"
        />
      </label>
      {saldo && (
        <p className="text-sm text-gray-600 md:col-span-2">
          Saldo atual:{" "}
          <strong>
            {saldo.saldoAtual} {saldo.unidadeSimbolo}
          </strong>
        </p>
      )}
    </>
  );
}

export default function EntradaManualNova({ modoInicial = "PEDIDO" }) {
  return modoInicial === "PEDIDO" ? (
    <EntradaPedidoLote />
  ) : (
    <EntradaManualAvulsa />
  );
}
